/**
 * My profile — the doula's own listing, and the national directory entry behind it.
 *
 * Trust's directory holds 23,120 doulas assembled from NPPES and DHCS. None of
 * them asked to be there, and it shows: language is recorded for 2.7% of them,
 * while language is a HARD FILTER in matching. The only person who knows the
 * answer is the doula herself, which is what this page is for.
 *
 * Two things the design deliberately does:
 *
 *   - separates what is true for MATCHING from what is true in the NATIONAL
 *     directory. Her edits reach mothers immediately; they reach the directory
 *     only once a person has verified that the listing is hers. Anyone can type
 *     an NPI, so a claim is an assertion until someone checks it;
 *   - never blocks her edit on the directory being reachable. Her correction is
 *     already true where it matters most, and a slow third system must not cost
 *     her the change.
 */
import { useState, useEffect, useCallback } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';
import { authFetchJson } from '@/lib/authFetch';
import { PageContainer, PageHeader, PageState } from '@/components/weavein';
import { AlertCircle, BadgeCheck, Check, Globe, Languages, Save } from 'lucide-react';

interface Me {
  panel_id: number;
  display_name: string;
  npi?: string | null;
  status: string;
  accepting_new_clients: boolean;
  caseload_ceiling?: number | null;
  active_clients: number;
  can: string[];
}

interface Listing {
  found: boolean;
  npi?: string | null;
  name?: string | null;
  city?: string | null;
  state?: string | null;
  languages_spoken: string[];
  claim_status?: string | null;
  can_claim: boolean;
  message?: string | null;
}

/** Comma-separated in the box, a list on the wire. */
function toList(value: string): string[] {
  return value
    .split(',')
    .map((s) => s.trim())
    .filter(Boolean);
}

export default function DoulaProfilePage() {
  const [me, setMe] = useState<Me | null>(null);
  const [listing, setListing] = useState<Listing | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState<string | null>(null);

  const [languages, setLanguages] = useState('');
  const [communities, setCommunities] = useState('');
  const [specialisms, setSpecialisms] = useState('');
  const [bio, setBio] = useState('');
  const [pronouns, setPronouns] = useState('');
  const [acceptingNew, setAcceptingNew] = useState(true);
  const [evidence, setEvidence] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const mine = await authFetchJson<Me>('/api/doula/v1/me');
      setMe(mine);
      setAcceptingNew(mine.accepting_new_clients);

      // The directory is a separate service. If it is slow or down the rest of
      // this page still works, so its failure is caught here rather than
      // aborting the load.
      try {
        setListing(await authFetchJson<Listing>('/api/doula/v1/directory-listing'));
      } catch {
        setListing(null);
      }
    } catch (e) {
      setError(e instanceof Error ? e.message : 'We could not load your profile.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const save = async () => {
    setSaving(true);
    setSaved(null);
    setError(null);
    try {
      const body: Record<string, unknown> = { accepting_new_clients: acceptingNew };
      if (languages.trim()) body.languages = toList(languages);
      if (communities.trim()) body.communities = toList(communities);
      if (specialisms.trim()) body.specialisms = toList(specialisms);
      if (bio.trim()) body.bio = bio.trim();
      if (pronouns.trim()) body.pronouns = pronouns.trim();

      const res = await authFetchJson<{ message: string; directory: { pushed: boolean; reason?: string } }>(
        '/api/doula/v1/profile',
        { method: 'PUT', body: JSON.stringify(body) },
      );
      setSaved(
        res.directory?.pushed
          ? 'Saved, and sent to the national directory.'
          : res.message || 'Saved.',
      );
      await load();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'We could not save your changes.');
    } finally {
      setSaving(false);
    }
  };

  const claim = async () => {
    setSaving(true);
    setError(null);
    try {
      await authFetchJson('/api/doula/v1/directory-listing/claim', {
        method: 'POST',
        body: JSON.stringify({ evidence: evidence.trim() || null }),
      });
      setSaved('Thank you. Someone will check this and your listing will show as yours.');
      setEvidence('');
      await load();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'We could not record your claim.');
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <PageState variant="loading" />;

  if (error && !me) {
    return (
      <PageContainer>
        <PageHeader title="My profile" />
        <Alert>
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      </PageContainer>
    );
  }

  const claimStatus = listing?.claim_status;

  return (
    <PageContainer>
      <PageHeader
        title="My profile"
        description="What mothers see when they are choosing a doula, and what the national directory says about you."
      />

      {error && (
        <Alert className="mb-4">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}
      {saved && (
        <Alert className="mb-4">
          <Check className="h-4 w-4" />
          <AlertDescription>{saved}</AlertDescription>
        </Alert>
      )}

      {/* What matters most, first: language is a hard filter, so an empty value
          here is the difference between being matched and being invisible. */}
      <Card className="mb-6">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Languages className="h-5 w-5" />
            How you are matched
          </CardTitle>
          <CardDescription>
            A mother is only shown doulas who speak her language. If your languages are missing here,
            you will not appear for her at all.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div>
            <Label htmlFor="languages">Languages you speak</Label>
            <Input
              id="languages"
              value={languages}
              onChange={(e) => setLanguages(e.target.value)}
              placeholder="English, Spanish"
            />
            <p className="mt-1 text-sm text-muted-foreground">Separate them with commas.</p>
          </div>

          <div>
            <Label htmlFor="communities">Communities you serve</Label>
            <Input
              id="communities"
              value={communities}
              onChange={(e) => setCommunities(e.target.value)}
              placeholder="Latina, Black"
            />
          </div>

          <div>
            <Label htmlFor="specialisms">What you specialise in</Label>
            <Input
              id="specialisms"
              value={specialisms}
              onChange={(e) => setSpecialisms(e.target.value)}
              placeholder="first birth, VBAC, lactation, perinatal loss"
            />
          </div>

          <div className="flex items-center justify-between rounded-md border p-3">
            <div>
              <Label htmlFor="accepting">Taking new clients</Label>
              <p className="text-sm text-muted-foreground">
                {me?.active_clients ?? 0} active
                {me?.caseload_ceiling ? ` of ${me.caseload_ceiling}` : ''}
              </p>
            </div>
            <Switch id="accepting" checked={acceptingNew} onCheckedChange={setAcceptingNew} />
          </div>
        </CardContent>
      </Card>

      <Card className="mb-6">
        <CardHeader>
          <CardTitle>In your own words</CardTitle>
          <CardDescription>A mother reads this before she chooses.</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div>
            <Label htmlFor="pronouns">Pronouns</Label>
            <Input
              id="pronouns"
              value={pronouns}
              onChange={(e) => setPronouns(e.target.value)}
              placeholder="she/her"
            />
          </div>
          <div>
            <Label htmlFor="bio">About you</Label>
            <Textarea
              id="bio"
              value={bio}
              onChange={(e) => setBio(e.target.value)}
              rows={5}
              placeholder="How you work, what you bring, anything you want her to know."
            />
          </div>
        </CardContent>
      </Card>

      {/* The directory is a different thing from her matching profile, and the
          page says so rather than blurring them. */}
      <Card className="mb-6">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Globe className="h-5 w-5" />
            Your national directory listing
          </CardTitle>
          <CardDescription>
            Trust keeps a national directory built from public records. Claiming your listing lets your
            corrections reach it.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {!me?.npi && (
            <Alert>
              <AlertCircle className="h-4 w-4" />
              <AlertDescription>
                We do not have your NPI, so we cannot find your listing. Ask your programme to add it.
              </AlertDescription>
            </Alert>
          )}

          {me?.npi && !listing?.found && (
            <Alert>
              <AlertCircle className="h-4 w-4" />
              <AlertDescription>
                {listing?.message || 'We could not reach the directory just now. Your other changes still save.'}
              </AlertDescription>
            </Alert>
          )}

          {listing?.found && (
            <div className="rounded-md border p-4">
              <div className="flex items-start justify-between gap-3">
                <div>
                  <p className="font-medium">{listing.name}</p>
                  <p className="text-sm text-muted-foreground">
                    {[listing.city, listing.state].filter(Boolean).join(', ')}
                    {listing.npi ? ` · NPI ${listing.npi}` : ''}
                  </p>
                </div>
                {claimStatus === 'verified' && (
                  <Badge className="flex items-center gap-1">
                    <BadgeCheck className="h-3 w-3" />
                    Yours
                  </Badge>
                )}
                {claimStatus === 'pending' && <Badge variant="secondary">Being checked</Badge>}
              </div>

              <div className="mt-3">
                <p className="text-sm font-medium">Languages the directory has for you</p>
                {listing.languages_spoken.length ? (
                  <div className="mt-1 flex flex-wrap gap-1">
                    {listing.languages_spoken.map((l) => (
                      <Badge key={l} variant="outline">
                        {l}
                      </Badge>
                    ))}
                  </div>
                ) : (
                  <p className="mt-1 text-sm text-muted-foreground">
                    None recorded — which is true of 97% of the directory.
                  </p>
                )}
              </div>
            </div>
          )}

          {listing?.found && listing.can_claim && (
            <div className="space-y-3">
              <div>
                <Label htmlFor="evidence">Tell us how we can confirm this is you</Label>
                <Textarea
                  id="evidence"
                  value={evidence}
                  onChange={(e) => setEvidence(e.target.value)}
                  rows={3}
                  placeholder="A licence number, a practice address, anything that helps us check."
                />
              </div>
              <Button onClick={() => void claim()} disabled={saving}>
                This listing is mine
              </Button>
            </div>
          )}

          {claimStatus === 'pending' && (
            <p className="text-sm text-muted-foreground">
              We are checking your claim. Your changes above are already live for matching.
            </p>
          )}
        </CardContent>
      </Card>

      <div className="flex justify-end">
        <Button onClick={() => void save()} disabled={saving} className="flex items-center gap-2">
          <Save className="h-4 w-4" />
          {saving ? 'Saving…' : 'Save changes'}
        </Button>
      </div>
    </PageContainer>
  );
}
